import { useRef, useEffect, useState, useCallback } from "react";
import Player from "@vimeo/player";
import {
  Play,
  Pause,
  SpeakerHigh,
  SpeakerSlash,
  CornersOut,
  CornersIn,
  GearSix,
  Clock,
  Subtitles,
} from "@phosphor-icons/react";
import clsx from "clsx";

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

type MenuType = "speed" | "quality" | null;

interface VimeoTextTrack {
  label: string;
  language: string;
  kind: string;
  mode: string;
}

interface VimeoQuality {
  id: string;
  label: string;
  active: boolean;
}

export default function VimeoPlayer({
  videoId,
  autoplay = false,
  onDuration,
  onEnd,
}: {
  videoId: string;
  autoplay?: boolean;
  onDuration?: (seconds: number) => void;
  onEnd?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [activeMenu, setActiveMenu] = useState<MenuType>(null);
  const [textTracks, setTextTracks] = useState<VimeoTextTrack[]>([]);
  const [activeTrack, setActiveTrack] = useState<string | null>(null);
  const [qualities, setQualities] = useState<VimeoQuality[]>([]);
  const [activeQuality, setActiveQuality] = useState("auto");

  const hideTimer = useRef<ReturnType<typeof setTimeout>>();
  const progressRef = useRef<HTMLDivElement>(null);

  // Init player
  useEffect(() => {
    if (!containerRef.current) return;

    const player = new Player(containerRef.current, {
      id: parseInt(videoId),
      autoplay: true,
      controls: false,
      responsive: false,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      dnt: true,
      title: false,
      byline: false,
      portrait: false,
      keyboard: true,
      pip: false,
      quality: "auto",
      transparent: false,
      background: false,
    });

    playerRef.current = player;

    player.on("play", () => setIsPlaying(true));
    player.on("pause", () => setIsPlaying(false));
    player.on("ended", () => {
      setIsPlaying(false);
      onEnd?.();
    });
    player.on("timeupdate", (data) => {
      setCurrentTime(data.seconds);
      setDuration(data.duration);
    });
    player.on("progress", (data) => {
      setBuffered(data.percent);
    });
    player.on("playbackratechange", (data) => {
      setSpeed(data.playbackRate);
    });
    player.on("loaded", () => {
      setIsReady(true);
      player.getDuration().then((d) => {
        setDuration(d);
        onDuration?.(d);
      });

      // Get text tracks (subtitles)
      player.getTextTracks().then((tracks: VimeoTextTrack[]) => {
        setTextTracks(tracks);
      });

      // Get qualities
      player.getQualities().then((q: VimeoQuality[]) => {
        setQualities(q);
        const active = q.find((x) => x.active);
        if (active) setActiveQuality(active.id);
      });

      // Autoplay
      player.play().catch(() => {});
    });

    return () => {
      player.destroy();
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Fullscreen change listener
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Auto-hide controls
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (isPlaying && !activeMenu) {
      hideTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying, activeMenu]);

  useEffect(() => {
    resetHideTimer();
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [isPlaying, resetHideTimer]);

  // Controls
  const togglePlay = () => {
    if (!playerRef.current) return;
    if (isPlaying) playerRef.current.pause();
    else playerRef.current.play();
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    if (isMuted) {
      playerRef.current.setVolume(1);
      setIsMuted(false);
    } else {
      playerRef.current.setVolume(0);
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    const wrapper = containerRef.current?.closest(".group\\/player");
    if (!wrapper) return;
    if (!document.fullscreenElement) {
      (wrapper as HTMLElement).requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const changeSpeed = (s: number) => {
    if (!playerRef.current) return;
    playerRef.current.setPlaybackRate(s);
    setSpeed(s);
    setActiveMenu(null);
  };

  const changeTrack = (lang: string | null) => {
    if (!playerRef.current) return;
    if (lang) {
      playerRef.current.enableTextTrack(lang);
    } else {
      playerRef.current.disableTextTrack();
    }
    setActiveTrack(lang);
    setActiveMenu(null);
  };

  const changeQuality = (q: string) => {
    if (!playerRef.current) return;
    playerRef.current.setQuality(q);
    setActiveQuality(q);
    setActiveMenu(null);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !playerRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    playerRef.current.setCurrentTime(pct * duration);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="relative w-full h-full group/player bg-black overflow-hidden"
      onMouseMove={resetHideTimer}
    >
      {/* Vimeo iframe */}
      <div ref={containerRef} className="absolute inset-0 [&_iframe]:!w-full [&_iframe]:!h-full [&_iframe]:!absolute [&_iframe]:!inset-0 [&_iframe]:!border-0" />

      {/* Click overlay */}
      <div className="absolute inset-0 z-[5]" onClick={togglePlay} />

      {/* Loading */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
          <div className="relative w-10 h-10">
            <div className="absolute inset-0 rounded-full border-2 border-white/[0.06]" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-myGreen animate-spin" />
          </div>
        </div>
      )}

      {/* Center play button */}
      {!isPlaying && isReady && (
        <div className="absolute inset-0 flex items-center justify-center z-[6] pointer-events-none">
          <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
            <Play size={32} weight="fill" className="text-white ml-1" />
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div
        className={clsx(
          "absolute bottom-0 left-0 right-0 z-[15] transition-opacity duration-300",
          showControls || !isPlaying ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-24 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
          {/* Progress bar */}
          <div
            ref={progressRef}
            className="h-1 hover:h-1.5 bg-white/15 rounded-full mb-3 cursor-pointer transition-all group/progress relative"
            onClick={handleProgressClick}
          >
            <div
              className="absolute top-0 left-0 h-full bg-white/10 rounded-full"
              style={{ width: `${buffered * 100}%` }}
            />
            <div
              className="absolute top-0 left-0 h-full bg-myGreen rounded-full"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-myGreen rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
              style={{ left: `${progress}%`, marginLeft: -6 }}
            />
          </div>

          {/* Controls row */}
          <div className="flex items-center gap-2.5">
            <button onClick={togglePlay} className="text-white hover:text-myGreen transition-colors cursor-pointer">
              {isPlaying ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
            </button>

            <button onClick={toggleMute} className="text-white/60 hover:text-white transition-colors cursor-pointer">
              {isMuted ? <SpeakerSlash size={18} /> : <SpeakerHigh size={18} />}
            </button>

            <span className="text-[11px] text-white/40 font-mono select-none">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <div className="flex-1" />

            {/* Speed */}
            <div className="relative">
              <button
                onClick={() => setActiveMenu(activeMenu === "speed" ? null : "speed")}
                className={clsx(
                  "flex items-center gap-1 text-[11px] font-semibold transition-colors cursor-pointer px-1.5 py-0.5 rounded",
                  speed !== 1 ? "text-myGreen" : "text-white/60 hover:text-white"
                )}
              >
                <Clock size={14} />
                {speed}x
              </button>
              {activeMenu === "speed" && (
                <div className="absolute bottom-8 right-0 bg-surface-1 border border-white/[0.08] rounded-xl py-1.5 min-w-[80px] shadow-xl">
                  {SPEED_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeSpeed(s)}
                      className={clsx(
                        "w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors",
                        s === speed ? "text-myGreen font-semibold" : "text-white/60 hover:text-white hover:bg-white/5"
                      )}
                    >
                      {s}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Subtitles */}
            {textTracks.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setActiveMenu(activeMenu === "quality" ? null : "quality")}
                  className={clsx(
                    "transition-colors cursor-pointer",
                    activeTrack ? "text-myGreen" : "text-white/60 hover:text-white"
                  )}
                >
                  <Subtitles size={18} />
                </button>
                {activeMenu === "quality" && textTracks.length > 0 && (
                  <div className="absolute bottom-8 right-0 bg-surface-1 border border-white/[0.08] rounded-xl py-1.5 min-w-[120px] shadow-xl">
                    <button
                      onClick={() => changeTrack(null)}
                      className={clsx(
                        "w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors",
                        !activeTrack ? "text-myGreen font-semibold" : "text-white/60 hover:text-white hover:bg-white/5"
                      )}
                    >
                      Kapalı
                    </button>
                    {textTracks.map((t) => (
                      <button
                        key={t.language}
                        onClick={() => changeTrack(t.language)}
                        className={clsx(
                          "w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors",
                          activeTrack === t.language ? "text-myGreen font-semibold" : "text-white/60 hover:text-white hover:bg-white/5"
                        )}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Quality */}
            {qualities.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setActiveMenu(activeMenu === "quality" ? null : "quality")}
                  className="text-white/60 hover:text-white transition-colors cursor-pointer"
                >
                  <GearSix size={18} />
                </button>
                {activeMenu === "quality" && (
                  <div className="absolute bottom-8 right-0 bg-surface-1 border border-white/[0.08] rounded-xl py-1.5 min-w-[100px] shadow-xl">
                    {qualities.map((q) => (
                      <button
                        key={q.id}
                        onClick={() => changeQuality(q.id)}
                        className={clsx(
                          "w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors",
                          activeQuality === q.id ? "text-myGreen font-semibold" : "text-white/60 hover:text-white hover:bg-white/5"
                        )}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button onClick={toggleFullscreen} className="text-white/60 hover:text-white transition-colors cursor-pointer">
              {isFullscreen ? <CornersIn size={18} /> : <CornersOut size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Close menus on click outside */}
      {activeMenu && (
        <div className="absolute inset-0 z-[14]" onClick={() => setActiveMenu(null)} />
      )}
    </div>
  );
}
